import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import dayjs from "dayjs";
import { selectUser, selectPartnersData, loadingPage } from "store/slices/app";
import * as api from "api/endpoints/fileValidation";
import * as batchApi from "api/endpoints/batchConfiguration";
import BatchConfigurationMain from "../components/batchSetup/BatchConfigurationMain";
import { selectPortfolios } from "store/slices/app";

export default function BatchConfigurationPage() {
  const [fieldMappingRows, setFieldMappingRows] = useState([]);
  const [batchStatusRecord, setBatchStatusRecord] = useState([]);
  const [permissions, setPermissions] = useState({});

  const [hasHeaderRow, setHasHeaderRow] = useState();
  const [inputFolder, setInputFolder] = useState(null);
  const [outputFolder, setOutputFolder] = useState(null);
  const [batchRetrieval, setBatchRetrieval] = useState(null);
  const [batchPlacement, setBatchPlacement] = useState(null);
  const [checkBoxValues, setCheckboxValues] = useState({
    showGrades: false,
    showNonMatches: false,
    addMatches: false,
    addNonMatches: false,
    businessMonitoring: false,
    complianceMonitoring: false
  });
  const [portfolioId, setPortfolioId] = useState();
  const [loading, setLoading] = useState(false);
  const [pageState, setPageState] = useState(1);
  const [showAlert, setShowAlert] = useState({
    monitoring: false,
    matching: false,
    fileRetrievalSftp: false,
    filePlacementSftp: false
  });
  const [showNotification, setShowNotification] = useState({ type: "", message: "" });
  const [showBatchStatusError, setBatchStatusError] = useState(false);
  const [selectedPortfolio, setSelectedPortfolio] = useState(null);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);

  const userData = useSelector(selectUser);
  const epId = userData.selectedPartner.id;
  const partnersData = useSelector(selectPartnersData);
  const portfolios = useSelector(selectPortfolios);
  const loadData = (userData.isSuperUser && partnersData.isPartnerChanged) || !userData.isSuperUser;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const folderError = inputFolder === outputFolder;

  const selectedDate = {
    fromDate: fromDate ? dayjs(fromDate) : null,
    toDate: toDate ? dayjs(toDate) : null
  };

  useEffect(() => {
    setShowNotification(false);
    setShowAlert(false);

    if (userData.environment === "production") {
      getMappingData(epId);
      getConfiguration(epId);
    }
  }, [epId, loadData, userData.environment]);

  useEffect(() => {
    setPageState(1);
  }, [userData.selectedPartner.id]);
  const showBatchCallback = useCallback(showBatchStatus, [fromDate, toDate, pageState, epId]);

  useEffect(() => {
    showBatchCallback();
  }, [userData.selectedPartner.id, showBatchCallback]);

  useEffect(() => {
    setFromDate(null);
    setToDate(null);
  }, [epId]);

  async function getMappingData(epId) {
    const mappingData = await api.getFieldMapping(epId);
    setFieldMappingRows(mappingData.details.fieldData || []);
    setHasHeaderRow(mappingData.details.containsHeader);
  }

  async function getConfiguration(epId) {
    try {
      const batchData = await batchApi.getBatchSettings(epId);
      setPermissions(batchData.permissions);
      const { details } = batchData;
      const advancedMatchFeatures = details.advancedMatchFeatures;
      const enableMonitoring = details.enableMonitoring;
      const portfolioSelection = details.portfolioSelection;
      const batchFileRetrieval = details.batchFileRetrieval;
      const batchFilePlacement = details.batchFilePlacement;
      const portfolioDetails = portfolioSelection.portfolioDetails;

      setCheckboxValues((prevValues) => ({
        ...prevValues,
        showGrades: advancedMatchFeatures.showGrades || false,
        showNonMatches: advancedMatchFeatures.showNonMatches || false,
        complianceMonitoring: enableMonitoring.complianceMonitoring || false,
        businessMonitoring: enableMonitoring.businessMonitoring || false,
        addMatches: portfolioSelection.addMatches || false,
        addNonMatches: portfolioSelection.addNonMatches || false
      }));

      setBatchRetrieval(batchFileRetrieval.mySftp ? "sftpInputFolder" : "mySftpBatchRetrieval");
      setBatchPlacement(batchFilePlacement.mySftp ? "sftpOutputFolder" : "mySftpBatchPlacement");
      setInputFolder(batchFileRetrieval.sftpInputFolder || "");
      setOutputFolder(batchFilePlacement.sftpOutputFolder || "");
      setSelectedPortfolio(portfolioDetails.portfolioName || "");
      setPortfolioId(portfolioDetails.portfolioId);
    } catch (error) {
      const msg = error.response?.data?.errors || "Something went wrong";
      console.log("error", msg);
    }
  }

  function handlePageChange(e, value) {
    setPageState(value);
  }

  async function updateConfiguration() {
    dispatch(loadingPage(true));
    setLoading(true);
    setShowNotification(false);
    setShowAlert(false);
    const mySftpBatchRetrieval = batchRetrieval === "sftpInputFolder";
    const mySftpBatchPlacement = batchPlacement === "sftpOutputFolder";

    if ((mySftpBatchRetrieval && !mySftpBatchPlacement) || (!mySftpBatchRetrieval && mySftpBatchPlacement)) {
      setShowNotification({
        type: "error",
        message: "Batch File Retrieval and Placement can either be both Markaaz's SFTP or be My SFTP."
      });
      setLoading(false);
      dispatch(loadingPage(false));
      return;
    }

    try {
      const data = {
        ...checkBoxValues,
        mySftpBatchRetreival: mySftpBatchRetrieval,
        sftpOutputFolder: outputFolder,
        mySftpBatchPlacement: mySftpBatchPlacement,
        sftpInputFolder: inputFolder,
        portfolioId: portfolioId
      };

      await batchApi.updateBatchSettings(data, epId).then();
      getConfiguration(epId);
      setShowNotification({ type: "success", message: "Batch Configurations Saved" });
    } catch (error) {
      const msg = error.response?.data?.errors || "Something went wrong";
      setShowNotification({ type: "error", message: msg });
    } finally {
      setLoading(false);
      dispatch(loadingPage(false));
    }
  }

  function handleFromDateSelect(value) {
    const date = value ? dayjs(value.toDate()).format() : null;
    setFromDate(date);
    setPageState(1);
  }

  function handleToDateSelect(value) {
    const date = value ? dayjs(value.toDate()).format() : null;
    setToDate(date);
    setPageState(1);
  }

  async function showBatchStatus() {
    const formattedFromDate =
      (fromDate ? dayjs(fromDate).format("YYYY-MM-DD") : dayjs().subtract(1, "year").format("YYYY-MM-DD")) +
      "T00:00:00.000Z";
    const formattedToDate =
      (toDate ? dayjs(toDate).format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD")) + "T23:59:59.000Z";

    const data = {
      pageNo: pageState,
      pageSize: 10,
      epId: epId,
      fromDateTime: formattedFromDate,
      toDateTime: formattedToDate
    };

    const bathStatus = await batchApi.getBatchStatus(data);
    setBatchStatusRecord(bathStatus);
    setBatchStatusError(bathStatus.details.length === 0);
  }

  function handleCheckBox(e) {
    const { name, checked } = e.target;
    const values = { ...checkBoxValues };

    values[name] = checked;

    if (
      (name === "addMatches" && !checked && !values.addNonMatches) ||
      (name === "addNonMatches" && !checked && !values.addMatches)
    ) {
      values.businessMonitoring = false;
      values.complianceMonitoring = false;
    }

    if (name === "showNonMatches" && !checked) {
      values.addNonMatches = false;

      if (!values.addMatches) {
        values.businessMonitoring = false;
        values.complianceMonitoring = false;
      }
    }

    setCheckboxValues(values);
  }

  function handleBatchRetrieval(event) {
    setBatchRetrieval(event.target.value);
  }

  function handleBatchPlacement(event) {
    setBatchPlacement(event.target.value);
  }

  function handlePortfolioChange(_, value) {
    setPortfolioId(value?.id || null);
    setSelectedPortfolio(value?.label || null);
  }

  function handleInputFolder(event) {
    setInputFolder(event.target.value);
  }

  function handleOutputFolder(event) {
    setOutputFolder(event.target.value);
  }

  function handleShowAlert(name) {
    const alertState = {
      monitoring: false,
      matching: false,
      fileRetrievalSftp: false,
      filePlacementSftp: false
    };

    if (alertState.hasOwnProperty(name)) {
      alertState[name] = true;
    }

    setShowAlert(alertState);
    setShowNotification(false);
  }

  function handleAlertClose(name) {
    const defaultAlertState = {
      monitoring: false,
      matching: false,
      fileRetrievalSftp: false,
      filePlacementSftp: false
    };

    switch (name) {
      case "monitoring":
      case "matchConfig":
      case "fileRetrievalSftp":
      case "filePlacementSftp":
        setShowAlert(defaultAlertState);
        break;
      case "saveConfig":
        setShowNotification({
          type: "",
          message: ""
        });
        break;
      case "jobStatus":
        setBatchStatusError(false);
        setFromDate(null);
        setToDate(null);
        break;
      default:
        break;
    }
  }

  function handlePageRoute() {
    navigate("/batch/file-validation");
  }

  function handleReloadStatus() {
    showBatchStatus(pageState, epId, fromDate, toDate);
  }

  const pagination = {
    page: pageState,
    count: batchStatusRecord.page_count,
    onChange: handlePageChange
  };
  return (
    <BatchConfigurationMain
      checkBoxValues={checkBoxValues}
      fieldMappingRows={fieldMappingRows}
      hasHeaderRow={hasHeaderRow}
      permissions={permissions}
      inputFolder={inputFolder}
      outputFolder={outputFolder}
      portfolios={portfolios}
      selectedPortfolio={selectedPortfolio}
      batchStatusRecord={batchStatusRecord}
      batchRetrieval={batchRetrieval}
      batchPlacement={batchPlacement}
      loading={loading}
      showAlert={showAlert}
      showNotification={showNotification}
      showBatchStatusError={showBatchStatusError}
      selectedDate={selectedDate}
      isPartnerChanged={!loadData}
      folderError={folderError}
      pagination={pagination}
      onPageRoute={handlePageRoute}
      onDisabledSection={handleShowAlert}
      onAlertClose={handleAlertClose}
      onBatchRetrieval={handleBatchRetrieval}
      onBatchPlacement={handleBatchPlacement}
      onCheckBoxChange={handleCheckBox}
      onUpdate={updateConfiguration}
      onInputFolder={handleInputFolder}
      onOutputFolder={handleOutputFolder}
      onPortfolioChange={handlePortfolioChange}
      onFromDateSelect={handleFromDateSelect}
      onToDateSelect={handleToDateSelect}
      onHandleRefresh={handleReloadStatus}
    />
  );
}
